// src/components/authorizations/CredentialConnectionForm.tsx
//
// The connect form for every API-key connector with a generic credential form
// (catalog plan 2026-10-13, step 0.5). The fields come from the backend
// (`GET /authorizations/connectors/{type}/credential-form`); this component
// renders them by kind and has no per-vendor branch.
//
// Rendered INSIDE the setup wizard's modal (not in a drawer: a drawer opened
// from a modal sits under the modal's overlay and its focus trap, so its
// fields cannot be used). Fill in, Test connection, then save.
//
// A failed test (or create) answers with the backend's own diagnosis, an
// exact sentence that tells the customer what to fix ("This API key has no
// Read permission..."). It is shown verbatim in a red Alert that stays on
// screen, not in a toast that disappears while the customer is still in the
// vendor's admin fixing it.
import { useCallback, useEffect, useState, type ChangeEvent } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  PasswordInput,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  createConnectorAuthorization,
  getCredentialForm,
  testConnectorCredentials,
  type CredentialField,
  type CredentialForm,
  type CredentialTestResult,
  type CredentialValues,
} from "@/api/authorizations";

type Props = {
  /** Registry connector type, e.g. "WOOCOMMERCE". */
  connectorType: string;
  /** Called after the connection was created. */
  onCreated: () => void;
  /** Shown as a Cancel button when set (e.g. when other connections exist). */
  onCancel?: () => void;
};

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/** A toggle's value: what the customer set, else the field's default. */
function toggleValue(field: CredentialField, values: CredentialValues): string {
  return values[field.name] ?? (field.default ? "true" : "false");
}

/** Trimmed, non-empty values only: an optional field left blank is not sent.
 *  A toggle is always sent, so the choice the form showed is the one tested. */
function trimmedValues(
  fields: CredentialField[],
  values: CredentialValues,
): CredentialValues {
  const out: CredentialValues = {};
  for (const f of fields) {
    if (f.kind === "toggle") {
      out[f.name] = toggleValue(f, values);
      continue;
    }
    const v = (values[f.name] ?? "").trim();
    if (v) out[f.name] = v;
  }
  return out;
}

/** True when every required field in `fields` has a non-blank value. */
export function requiredFilled(
  fields: CredentialField[],
  values: CredentialValues,
): boolean {
  return fields.every((f) => !f.required || !!(values[f.name] ?? "").trim());
}

type FieldInputProps = {
  field: CredentialField;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Prefix for the label, e.g. "New" in the rotate form. */
  labelPrefix?: string;
};

/** One credential field, rendered by its kind. */
export function CredentialFieldInput({
  field,
  value,
  onChange,
  disabled,
  labelPrefix,
}: FieldInputProps) {
  const label = labelPrefix
    ? `${labelPrefix} ${field.label.charAt(0).toLowerCase()}${field.label.slice(1)}`
    : field.label;
  const common = {
    label,
    placeholder: field.placeholder || undefined,
    description: field.help || undefined,
    value,
    onChange: (e: ChangeEvent<HTMLInputElement>) =>
      onChange(e.currentTarget.value),
    required: field.required,
    maxLength: field.max_length,
    disabled,
    autoComplete: "off",
  };

  if (field.kind === "toggle") {
    return (
      <Switch
        label={label}
        description={field.help || undefined}
        checked={value === "true"}
        onChange={(e) => onChange(e.currentTarget.checked ? "true" : "false")}
        disabled={disabled}
      />
    );
  }
  if (field.kind === "secret") {
    return <PasswordInput {...common} />;
  }
  if (field.kind === "host_label" && field.host_suffix) {
    const suffix = field.host_suffix;
    return (
      <TextInput
        {...common}
        rightSection={
          <Text size="sm" c="dimmed" data-testid={`host-suffix-${field.name}`}>
            {suffix}
          </Text>
        }
        rightSectionWidth={Math.max(48, suffix.length * 8 + 16)}
        rightSectionPointerEvents="none"
      />
    );
  }
  // text, customer_origin, and a host_label without a suffix.
  return <TextInput {...common} />;
}

/** Fetch a connector's credential form; `reload` retries after a failure. */
export function useCredentialForm(connectorType: string | null) {
  const [form, setForm] = useState<CredentialForm | null>(null);
  const [loading, setLoading] = useState(!!connectorType);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    if (!connectorType) {
      setForm(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setForm(await getCredentialForm(connectorType));
    } catch (err: unknown) {
      setForm(null);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [connectorType]);

  useEffect(() => {
    load();
  }, [load]);

  return { form, loading, error, reload: load };
}

export function CredentialConnectionForm({
  connectorType,
  onCreated,
  onCancel,
}: Props) {
  const { form, loading, error, reload } = useCredentialForm(connectorType);
  const [name, setName] = useState("");
  const [values, setValues] = useState<CredentialValues>({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<CredentialTestResult | null>(
    null,
  );
  const [errorText, setErrorText] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <Stack align="center" justify="center" py={40}>
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading the connection form...
        </Text>
      </Stack>
    );
  }

  if (error || !form) {
    return (
      <Stack gap="sm">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          title="Could not load the connection form"
        >
          {errorMessage(error, "Something went wrong. Try again.")}
        </Alert>
        <Group justify="flex-end">
          {onCancel && (
            <Button variant="default" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button variant="light" onClick={reload}>
            Try again
          </Button>
        </Group>
      </Stack>
    );
  }

  const fields = form.fields;

  // Any edit to what was tested invalidates the test.
  const setValue = (fieldName: string, value: string) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }));
    setTestResult(null);
    setErrorText(null);
  };

  const canTest = requiredFilled(fields, values);

  const handleTest = async () => {
    if (!canTest) return;
    setTesting(true);
    setTestResult(null);
    setErrorText(null);
    try {
      const result = await testConnectorCredentials(
        connectorType,
        trimmedValues(fields, values),
      );
      setTestResult(result);
      if (!name.trim() && result.default_name) setName(result.default_name);
    } catch (err: unknown) {
      setErrorText(errorMessage(err, "Connection test failed"));
    } finally {
      setTesting(false);
    }
  };

  const canSubmit = !!testResult && !!name.trim() && canTest;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorText(null);
    try {
      await createConnectorAuthorization(connectorType, {
        name: name.trim(),
        values: trimmedValues(fields, values),
      });
      notifications.show({
        color: "green",
        message: `${form.label} connection created`,
      });
      onCreated();
    } catch (err: unknown) {
      setErrorText(errorMessage(err, "Failed to create authorization"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack gap="md">
      {form.instructions && (
        <Text size="sm" c="dimmed">
          {form.instructions}
        </Text>
      )}

      {fields.map((field) => (
        <CredentialFieldInput
          key={field.name}
          field={field}
          value={
            field.kind === "toggle"
              ? toggleValue(field, values)
              : (values[field.name] ?? "")
          }
          onChange={(v) => setValue(field.name, v)}
        />
      ))}

      <Button
        variant="light"
        onClick={handleTest}
        loading={testing}
        disabled={!canTest}
      >
        Test connection
      </Button>

      {testResult && (
        <Alert icon={<IconCheck size={16} />} color="green" variant="light">
          {testResult.summary}
        </Alert>
      )}

      {testResult?.warning && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="yellow"
          variant="light"
          title="Check this permission"
        >
          {testResult.warning}
        </Alert>
      )}

      {errorText && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          title="Could not connect"
        >
          {errorText}
        </Alert>
      )}

      <TextInput
        label="Connection name"
        placeholder={`My ${form.label} connection`}
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        required
      />

      <Group justify="flex-end">
        {onCancel && (
          <Button variant="default" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          loading={submitting}
          disabled={!canSubmit}
        >
          Save connection
        </Button>
      </Group>
    </Stack>
  );
}
